import { action } from 'mobx';
import {
    EventHubPageType,
    ICreateHubPageDTO,
    ICustomHubPageDTO,
    IUpdateHubPageDTO,
} from '../../../../../../../api/event-hubs.dao';
import { AbstractFormViewModelController } from '../../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { CUSTOM_PAGE_CONFIG, DESCRIPTION, DISPLAY_NAME, ENABLED, PAGE_NAME, PAGE_PATH, uniquePagePathValidator } from './custom-page.config';
import { FormVmItemConfigType } from '../../../../../../../infrastructure/form-viewmodel-controller';
import { ModulesVM } from '../_modules/modules.viewmodel';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

export class CustomPageVM extends AbstractFormViewModelController {
    public readonly modulesVM = new ModulesVM();

    private static buildConfig(isExistedPath: (path: string) => boolean): FormVmItemConfigType[] {
        return CUSTOM_PAGE_CONFIG.map(item =>
            item.name === PAGE_PATH
                ? { ...item, validators: [...item.validators, uniquePagePathValidator(isExistedPath)] }
                : item
        );
    }

    public static createEmpty(isExistedPath: (path: string) => boolean): CustomPageVM {
        return new CustomPageVM(null, isExistedPath);
    }

    public static createFromPage(page: ICustomHubPageDTO, isExistedPath: (path: string) => boolean): CustomPageVM {
        const transpiledDescription = transpileInlineStylesToClasses(page.description);
        const editablePage = { ...page, description: transpiledDescription };
        return new CustomPageVM(editablePage, isExistedPath);
    }

    private constructor(private readonly page: ICustomHubPageDTO | null, isExistedPath: (path: string) => boolean) {
        super(page ?? {}, 'CustomPageVM', CustomPageVM.buildConfig(isExistedPath));
    }

    public hasUnsavedChanges(): boolean {
        return this.isVmChanged() || this.modulesVM.hasModuleChanges();
    }

    public dispose(): void {
        this.modulesVM.dispose();
    }

    @action
    public discardChanges = (): void => {
        this.resetVm();
        this.modulesVM.discardChanges();
    };

    public toCreateDTO(): ICreateHubPageDTO {
        const description = this.getTrimmedStringValue(DESCRIPTION);
        const transpiledDescription = transpileClassesToInlineStyles(description);

        return {
            pageName: this.getTrimmedStringValue(PAGE_NAME),
            displayName: this.getTrimmedStringValue(DISPLAY_NAME),
            pagePath: this.getTrimmedStringValue(PAGE_PATH),
            description: transpiledDescription,
            enabled: this.getFormVmValuePropsByKey(ENABLED).value as boolean,
            pageType: EventHubPageType.CUSTOM,
            ...this.modulesVM.buildModulesBatch(),
        };
    }

    public toUpdateDTO(): IUpdateHubPageDTO {
        return {
            ...this.toCreateDTO(),
            hubPageId: this.page.hubPageId,
        };
    }

    private getTrimmedStringValue(key: string): string {
        return (this.getFormVmValuePropsByKey(key).value as string).trim();
    }
}
